import { Field, Role, ROLE_RANK, SchemaAction } from '../schema';

/** Which of the three forms is being rendered. The `kind` of `ConfigScope`, without its payload. */
export type FormScope = 'device' | 'bulk' | 'site';

/** Who the user is. Deliberately nothing about which form they are looking at -- that is `FormScope`. */
export interface GatingContext {
  role: Role;
  permissions: string[];
}

export const meetsRole = (required: Role | undefined, current: Role) => {
  if (!required) return true;
  return ROLE_RANK[current] >= ROLE_RANK[required];
};

export const meetsPermissions = (required: string[] | undefined, held: string[]) => {
  if (!required || required.length === 0) return true;
  return required.every((p) => held.includes(p));
};

export const canViewField = (field: Field, ctx: GatingContext): boolean => {
  return meetsRole(field.required_role, ctx.role) && meetsPermissions(field.required_permissions, ctx.permissions);
};

/**
 * Whether a field or an option applies to the device type this form is being filled in for.
 *
 * A separate argument rather than part of `GatingContext`, for the reason `meetsScope` is: this is
 * a fact about the form, not about the user. A capability stays a declaration this way -- a GPS
 * perimeter is a wristwatch parameter, so the choice that writes one is simply not offered
 * elsewhere, rather than offered and then corrected.
 */
export const appliesToDeviceType = (onlyFor: string[] | undefined, deviceType: string | undefined): boolean => {
  if (!onlyFor || onlyFor.length === 0) return true;
  if (!deviceType) return true;
  return onlyFor.includes(deviceType);
};

export const meetsScope = (required: string[] | undefined, current: FormScope) => {
  if (!required || required.length === 0) return true;
  return required.includes(current);
};

// The scope is a separate argument rather than part of `ctx`: it is a fact about the form, not
// about the user, and the form already knows it. Folding it into `GatingContext` made every
// container restate something it had already said through the `scope` prop, with nothing keeping
// the two honest.
export const canRunAction = (action: SchemaAction, ctx: GatingContext, scope: FormScope): boolean => {
  return (
    meetsRole(action.required_role, ctx.role) &&
    meetsPermissions(action.required_permissions, ctx.permissions) &&
    meetsScope(action.scopes, scope)
  );
};
